import { useEffect, useRef } from 'react';
import { cn } from '@rocco/utils/cn';
import { useConversation } from '../../context';
import type { Participant } from '../../types';
import { getParticipantName } from '../../utils';
import { ParticipantAvatar } from '../avatar/ParticipantAvatar';

/**
 * Mention suggestion dropdown shown above the chat input.
 * @see {@link ChatInput}
 */
export function ChatInputMentionDropdown({
    className,
    participants,
    selectedMentionIndex,
    onSelectMention,
}: {
    className?: string;
    participants: Participant[];
    selectedMentionIndex: number;
    onSelectMention: (participant: Participant) => void;
}) {
    const listRef = useRef<HTMLUListElement>(null);

    // Keep the selected mention visible inside this list only. Avoid
    // `scrollIntoView`, which can scroll ancestor containers and shift the
    // chat input layout when the dropdown opens above the composer.
    useEffect(() => {
        const list = listRef.current;
        const selectedElement = list?.querySelector<HTMLElement>(
            `[data-mention-index="${selectedMentionIndex}"]`,
        );
        if (!list || !selectedElement) {
            return;
        }

        const listRect = list.getBoundingClientRect();
        const selectedRect = selectedElement.getBoundingClientRect();
        if (selectedRect.top < listRect.top) {
            list.scrollTop -= listRect.top - selectedRect.top;
        } else if (selectedRect.bottom > listRect.bottom) {
            list.scrollTop += selectedRect.bottom - listRect.bottom;
        }
    }, [selectedMentionIndex]);

    if (participants.length === 0) {
        return null;
    }

    return (
        <ul
            ref={listRef}
            className={cn(
                'flex max-h-[260px] flex-col gap-[2px] overflow-y-auto rounded-[8px] bg-white p-[4px] text-black shadow-lg',
                className,
            )}
            role="listbox"
            aria-label="Mention suggestions"
        >
            {participants.map((participant, index) => (
                <ChatInputMentionDropdownItem
                    key={participant.ID}
                    index={index}
                    participant={participant}
                    isSelected={index === selectedMentionIndex}
                    onSelectMention={onSelectMention}
                />
            ))}
        </ul>
    );
}

function ChatInputMentionDropdownItem({
    index,
    participant,
    isSelected,
    onSelectMention,
}: {
    index: number;
    participant: Participant;
    isSelected: boolean;
    onSelectMention: (participant: Participant) => void;
}) {
    const { selfParticipant } = useConversation();
    const participantName = getParticipantName(participant);
    const isSelfParticipant = participant.ID === selfParticipant?.ID;

    return (
        <li
            className={cn(
                'flex w-full cursor-pointer items-center gap-[8px] rounded-[8px] px-[8px] py-[6px] text-[13px] hover:bg-black/10',
                isSelected && 'bg-black/10',
            )}
            role="option"
            aria-selected={isSelected}
            data-mention-index={index}
            // Prevent focusing the dropdown.
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onSelectMention(participant)}
        >
            <ParticipantAvatar
                className="size-[24px]"
                fallbackClassName="text-[11px]!"
                participant={participant.Identity}
            />
            <span className="min-w-0 flex-1 truncate">
                @{participantName}
                {isSelfParticipant && ' (you)'}
            </span>
        </li>
    );
}
